import { Injectable } from '@angular/core';
import { HttpParams, HttpEventType, HttpClient, HttpHeaders, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, Subject, BehaviorSubject, ReplaySubject, forkJoin, throwError } from 'rxjs';

import { PostsDataUrls } from '../global.urls';
import * as Classes from '../classes/models.classes';
import { ShortsPostSave } from '../interfaces/models.interfaces';
import { map, tap } from 'rxjs/operators';

//import { Http } from '@angular/http';

/** Public homepage trending row (API uses PascalCase JSON). */
export interface TrendingHomePostDto {
  UserName?: string;
  userName?: string;
  ProfileName?: string;
  profileName?: string;
  UserImg?: string;
  userImg?: string;
  PostContent?: string;
  postContent?: string;
  Likes?: number;
  likes?: number;
  Reposts?: number;
  reposts?: number;
  Comments?: number;
  comments?: number;
  CategoryTag?: string;
  categoryTag?: string;
}

@Injectable({
  providedIn: 'any'
})
export class UserPostsDataService {
    lcl_params: Classes.UserID_Params;
    private postsSubject = new BehaviorSubject<Classes.ShortsPosts[]>([]); // Observable stream for posts
    private reactionsSubject = new BehaviorSubject<any>(null);  // For reactions

  constructor(
    private http: HttpClient) {

  }

    loadUserPostsData(userID: number, currentPage: number, pageSize: number, postTypes?: number[], query?: string): Observable<Classes.ShortsPosts[]> {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

        // Create request body with userID
        const body: any = { userID, currentPage, pageSize, postTypes, query };

        return this.http.post<any[]>(PostsDataUrls.GetUserPosts, body, { headers }).pipe(
            map(posts =>
                posts.map(post => {
                    // Pass the entire post object to the constructor
                    return new Classes.ShortsPosts({
                        ...post,
                        MediaItems: post.MediaItemsJson ? JSON.parse(post.MediaItemsJson) : [] // Parse MediaItemsJson if it exists
                    });
                })
            )
        );

    }

    getDashboardPosts(
        userID: number,
        currentPage: number,
        pageSize: number,
        postTypes?: number[],
        query?: string,
        feedScope: 'foryou' | 'following' = 'foryou'
    ): Observable<Classes.ShortsPosts[]> {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

        const body: any = { userID, currentPage, pageSize, postTypes, query, feedScope };

        //return this.http.post<Classes.ShortsPosts[]>(PostsDataUrls.GetDashboardPosts, body, { headers })
        //    .pipe(tap(posts => this.postsSubject.next(posts))); // Update the posts stream

        //return this.http.post<any[]>(PostsDataUrls.GetDashboardPosts, body, { headers }).pipe(
        //    map(posts =>
        //        posts.map(post => {
        //            // Parse MediaItemsJson if necessary
        //            const mediaItems = post.MediaItemsJson ? JSON.parse(post.MediaItemsJson) : [];
        //            return new Classes.ShortsPosts(mediaItems);
        //        })
        //    )
        //);

        return this.http.post<any[]>(PostsDataUrls.GetDashboardPosts, body, { headers }).pipe(
            map(posts =>
                posts.map(post => {
                    // Pass the entire post object to the constructor
                    return new Classes.ShortsPosts({
                        ...post,
                        MediaItems: post.MediaItemsJson ? JSON.parse(post.MediaItemsJson) : [] // Parse MediaItemsJson if it exists
                    });
                })
            )
        );
    }

    getPosts(userID: number, currentPage: number, pageSize: number, postTypes?: number[]): Observable<Classes.ShortsPosts[]> {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

        // Create request body with userID
        const body = { userID, currentPage, pageSize, postTypes };

        return this.http.post<Classes.ShortsPosts[]>(PostsDataUrls.GetUserPosts, body, { headers })
            .pipe(tap(posts => this.postsSubject.next(posts))); // Update the posts stream
    }

    getReplyPosts(userID: number, postID: number, currentPage: number, pageSize: number, postTypes?: number[]): Observable<Classes.ShortsPosts[]> {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

        // Create request body with userID
        const body = { userID, postID, currentPage, pageSize, postTypes };

        return this.http.post<Classes.ShortsPosts[]>(PostsDataUrls.GetUserReplyPosts, body, { headers })
            .pipe(tap(posts => this.postsSubject.next(posts))); // Update the posts stream
    }

    getPostsObservable(): Observable<Classes.ShortsPosts[]> {
        return this.postsSubject.asObservable(); // Return posts as an observable stream
    }

    getPost(userID: number, postID: number): Observable<Classes.ShortsPosts> {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

        // Create request body with userID
        const body = { userID, postID };

        //return this.http.post<Classes.ShortsPosts>(PostsDataUrls.GetUserPost, body, { headers }); 
        return this.http.post<any>(PostsDataUrls.GetUserPost, body, { headers }).pipe(
            map(post => {
                return new Classes.ShortsPosts({
                    ...post,
                    MediaItems: post.MediaItemsJson ? JSON.parse(post.MediaItemsJson) : [] // Ensure MediaItems is properly parsed
                });
            })
        );
    }

    getReactions(postId: number): Observable<any> {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const body = { postId };
        return this.http.post<any>(`${PostsDataUrls.GetReactions}`, body, { headers })
            .pipe(tap(reactions => this.reactionsSubject.next(reactions)));
    }

    search(query: string): Observable<{ Posts: Classes.SearchResult[], Users: Classes.SearchResult[] }> {
        const params = new HttpParams().set('Query', query);
        return this.http.get<{ Posts: Classes.SearchResult[], Users: Classes.SearchResult[] }>(PostsDataUrls.Search, { params });
    }

    toggleReaction(postId: number, userId: number, reactionType: string): Observable<any> {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const body = { postId, userId, reactionType };
        return this.http.post<any>(PostsDataUrls.ToggleReaction, body, { headers })
            .pipe(tap(reactions => this.reactionsSubject.next(reactions)));
    }

    saveUserRePostsData(postId: number, originalPostId: number, userId: number, reactionType: string, replyType: number, postContent: string, active: boolean): Observable<any> {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const body = { postId, originalPostId, userId, reactionType, replyType, postContent, active };
        return this.http.post<any>(PostsDataUrls.SaveUserRePosts, body, { headers })
            .pipe(tap(reactions => this.reactionsSubject.next(reactions)));
    }

    //saveQuotePost(postData: any, comment: string, file: File | null): Observable<any> {
    //    const formData = new FormData();
    //    formData.append('postData', JSON.stringify(postData));
    //    //formData.append('comment', comment);
    //    if (file) {
    //        formData.append('file', file);
    //    }
    //    return this.http.post<any>(PostsDataUrls.SaveQuotePost, formData);
    //}

    getReactionsObservable(): Observable<any> {
        return this.reactionsSubject.asObservable();
    }

    //saveUserPostsData(UserPostsDataContent: ShortsPostSave) {
    saveUserPostsData(UserPostsDataContent: FormData) {
        //let requestHeaders: HttpHeaders = new HttpHeaders();
        //requestHeaders = requestHeaders.append('Content-Type', 'application/json');

        //this.lcl_params = new Classes.UserID_Params();

        let saveUserPostsDataRequest: HttpRequest<FormData> = new HttpRequest<FormData>('POST', PostsDataUrls.SaveUserPosts, UserPostsDataContent, {
            reportProgress: true,
            //headers: requestHeaders
        });

        //return this.http.request<Array<Classes.ShortsPosts>>(saveUserPostsDataRequest);

        // Use HttpClient to send the request
        //return this.http.request(saveUserPostsDataRequest);
        return this.http.request<number>(saveUserPostsDataRequest);
    }

    saveQuotePost(UserPostsDataContent: FormData, UserID: number) {
        return this.http.post(PostsDataUrls.SaveQuotePost, UserPostsDataContent)
            .pipe(tap(() => this.refreshPosts(UserID))); // Refresh posts after a successful save

    //    let saveUserPostsDataRequest: HttpRequest<FormData> = new HttpRequest<FormData>('POST', PostsDataUrls.SaveQuotePost, UserPostsDataContent, {
    //        reportProgress: true,
    //    });

    //    // Use HttpClient to send the request
    //    return this.http.request<number>(saveUserPostsDataRequest);
    }

    savePost(UserPostsDataContent: FormData, UserID: number): Observable<any> {
        // TODO. Check to see if this is pulling the correct UserID. MB 6/7/24

        return this.http.post(PostsDataUrls.SaveUserPosts, UserPostsDataContent)
            .pipe(tap(() => this.refreshPosts(UserID))); // Refresh posts after a successful save
    }

    saveReply(UserPostsDataContent: FormData, UserID: number): Observable<any> {
        // TODO. Check to see if this is pulling the correct UserID. MB 6/7/24

        return this.http.post(PostsDataUrls.SaveReplyPost, UserPostsDataContent)
            .pipe(tap(() => this.refreshPosts(UserID))); // Refresh posts after a successful save
    }


    private refreshPosts(userID: number): void {
        let postTypes: number[] | undefined;
        postTypes = [1, 2, 3, 5, 20, 21]; // Specify the actual types you need

        this.loadUserPostsData(userID, 1, 10, postTypes).subscribe(); // Refresh posts by fetching new ones and updating the stream
    }

    /** Anonymous GET — homepage trending strip (no auth). */
    getTrendingHomePosts(): Observable<TrendingHomePostDto[]> {
        return this.http.get<TrendingHomePostDto[]>(PostsDataUrls.GetTrendingHomePosts).pipe(
            map((rows) => (Array.isArray(rows) ? rows : []))
        );
    }

}
